// Every free-text box asks the backend's ONE prefilter (MBTS
// src/intent/prefilter.js) through /api/screen, with the box's INPUT TYPE:
//   name      a person's name (first-name step, signature)
//   address   a street address or place
//   number    a price, quantity or amount
//   message   free text to another person (reviews, bids, need details)
//   captured  text stored as typed (task names, item names)
// There are no screening rules in the browser. If the server cannot be reached
// the text is allowed; the endpoint that receives the box checks it again.
export const FIELD_INPUT_TYPES = {
  name: "name", firstName: "name", legalName: "name", signature: "name",
  address: "address", place: "address", store: "address", fulfilledStore: "address", location: "address",
  task: "captured", item: "captured", items: "captured", food: "captured",
  protein: "captured", starch: "captured", vegetable: "captured",
  price: "number", quantity: "number", amount: "number",
  review: "message", message: "message", description: "message", topic: "message", bid: "message", details: "message",
};
const INPUT_TYPES = ["intent", "prompt", "captured", "name", "address", "number", "message"];
export const inputTypeFor = (field) => (INPUT_TYPES.includes(field) ? field : FIELD_INPUT_TYPES[field] || "message");

export async function screenText(text, field = "message") {
  try {
    const res = await fetch("/api/screen", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: String(text ?? ""), field, inputType: inputTypeFor(field) }),
    });
    if (!res.ok) return { ok: true };
    const data = await res.json();
    if (data && data.ok === false) return { ok: false, message: data.message || "That's a weird input. I don't get it. Try again." };
    // A name box also gets the name the backend took from the text ("Biz Barrett" -> Biz).
    return data && typeof data.name === "string" && data.name ? { ok: true, name: data.name } : { ok: true };
  } catch (_) {
    return { ok: true };
  }
}

// Screen several boxes; returns the first failure as { ok:false, field, message }.
export async function screenFields(pairs) {
  for (const [field, text] of pairs) {
    if (text === undefined || text === null || String(text).trim() === "") continue;
    const r = await screenText(text, field);
    if (!r.ok) return { ok: false, field, message: r.message };
  }
  return { ok: true };
}

// Food/activity ledger answers typed in chat, each step with its input type.
// The feeling steps (3, 4) are checked against their list; an empty answer is
// a skip. Everything else goes to the server, numbers and dates included.
export const LEDGER_INPUT_TYPES = {
  0: "message", // what was the activity
  1: "address", // where did it take place
  2: "message", // who were you with
  5: "message", // additional comments
  6: "captured", // what did you eat
  7: "address", // where did you eat (optional)
  8: "message", // describe the food
  9: "captured", // the food items
};
export async function screenLedgerAnswer(step, text) {
  const value = String(text ?? "").trim();
  if (step === 3 || step === 4) return { ok: true };
  if (!value) return { ok: true };
  return screenText(value, LEDGER_INPUT_TYPES[step] || "message");
}
